// Prices tab: the catalogs a price splits over (TD-075). The header's "Service lines" and "Work types" open a
// list in a drawer (DD-051), and the tab itself stays one table (DD-042). A row click replaces the list with the row's
// modal (DD-045): rename it, or delete it while nothing uses it (TD-099), confirmed in the footer. A service line's
// modal also holds its split defaults (TD-078), the work's shares and the agency's target in whole percents with the
// catch-up rate, saved with its name in one request. Each step is a URL param: ?modal=<catalog>, then &item=<id>.
// Back, a save and a delete hand back to the list, and X or Escape close both.
import { useState } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { FormField, inputClassName } from "@/components/ui/FormField";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { CatalogRowForm, type CatalogRow } from "../components/CatalogRowForm";
import { FinanceDrawer, type FinanceDrawerSize } from "../components/FinanceDrawer";
import { ModalPending, PendingBody, type Pending } from "../components/ModalPending";
import { PriceSplitEditor } from "../components/PriceSplitEditor";
import { useDeleteCatalogRow, useRenameCatalogRow, useServiceLines, useUpdateServiceLine, useWorkTypeCreator, useWorkTypes } from "../hooks/usePricing";
import { StaticNotice } from "../jobs/JobParts";
import type { Catalog } from "../services/pricingService";
import type { CatalogItem, ServiceLine } from "../types";
import { CATALOG_WORDS } from "../utils/catalogs";
import { defaultsCheck, defaultsForm, defaultsPatch, hasSplit, type DefaultsForm } from "../utils/splitDefaults";

const contractWord = ENTITY_CONFIG.contract.label.toLowerCase();
const SPLIT_HINT = `What a ${contractWord} line in this service line pays each work type by default, as a share of its price. The agency keeps the rest, its target. Remove every work type for no split defaults.`;
const CATCH_UP_HINT = "Of each price increase, the agency takes this share until it keeps its target. The rest goes to the work.";

function useCatalog(catalog: Catalog): UseQueryResult<CatalogItem[]> {
  const serviceLines = useServiceLines();
  const workTypes = useWorkTypes();
  return catalog === "work-types" ? workTypes : serviceLines;
}

/** While the rows are not loaded: a spinner, or the error with Retry. */
function pendingOf(catalog: Catalog, query: ReturnType<typeof useCatalog>): Pending {
  return {
    failure: query.isError ? `Could not load the ${CATALOG_WORDS[catalog].plural.toLowerCase()}.` : null,
    retrying: query.isFetching,
    retry: () => void query.refetch(),
  };
}

/** A catalog's drawer width: a service line holds its split defaults, so its list, its row and their loading take lg (DD-051). */
const WIDTH: Record<Catalog, FinanceDrawerSize> = { "service-lines": "lg", "work-types": "md" };

/** ?tab=prices&modal=<catalog>: every row, each opening its own drawer. */
export function CatalogListModal({ catalog, onOpen, onClose }: { catalog: Catalog; onOpen: (id: string) => void; onClose: () => void }) {
  const query = useCatalog(catalog);
  const words = CATALOG_WORDS[catalog];
  const rows = query.data;
  return (
    <FinanceDrawer onClose={onClose} size={WIDTH[catalog]} title={words.plural}>
      {!rows ? (
        <PendingBody {...pendingOf(catalog, query)} />
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-500">No {words.plural.toLowerCase()} yet. They are added on a listino line or a quote.</p>
      ) : (
        <div className="space-y-3">
          <ul className="divide-y divide-gray-100">
            {rows.map((row) => (
              <li key={row.id}>
                <button type="button" onClick={() => onOpen(row.id)} className="flex w-full items-center justify-between gap-3 rounded px-2 py-3 text-left hover:bg-gray-50 cursor-pointer">
                  <span className="min-w-0 truncate text-sm font-medium text-gray-900">{row.label}</span>
                  <span className="shrink-0 text-xs text-gray-500">{row.inUse ? "In use" : "Not used yet"}</span>
                </button>
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-500">Click one to rename it, or to delete it while nothing uses it.</p>
        </div>
      )}
    </FinanceDrawer>
  );
}

interface RowProps {
  catalog: Catalog;
  /** Back to the list, replacing this modal in the history. */
  onBack: () => void;
  /** Closes the row and the list. */
  onClose: () => void;
}

interface Removal {
  send: () => Promise<unknown>;
  pending: boolean;
}

/** ?tab=prices&modal=<catalog>&item=<id>: one row, renamed or deleted. A service line also holds its split defaults. */
export function CatalogRowModal({ catalog, id, onBack, onClose }: RowProps & { id: string }) {
  const query = useCatalog(catalog);
  const serviceLines = useServiceLines();
  const rename = useRenameCatalogRow();
  const remove = useDeleteCatalogRow();
  const words = CATALOG_WORDS[catalog];
  if (!query.data) return <ModalPending title={words.label} onClose={onClose} size={WIDTH[catalog]} {...pendingOf(catalog, query)} />;
  const row = query.data.find((r) => r.id === id);
  if (!row) {
    // Nothing to go back to it for: X and Escape close the list too.
    return <StaticNotice title={`${words.label} not found`} message={`This ${words.label.toLowerCase()} no longer exists.`} onClose={onClose} size={WIDTH[catalog]} />;
  }
  const removal: Removal = { send: () => remove.mutateAsync({ catalog, row }), pending: remove.isPending };
  // The same cache as `query` for service lines, read typed for its defaults.
  const line = catalog === "service-lines" ? serviceLines.data?.find((l) => l.id === id) : undefined;
  if (line) return <ServiceLineForm key={line.id} line={line} rows={query.data} remove={removal} onBack={onBack} onClose={onClose} />;
  return (
    <CatalogRowForm
      key={row.id}
      row={row}
      rows={query.data}
      noun={words.label}
      inUseNote={words.inUse}
      deleteQuestion={`Delete ${row.label}? Nothing uses it.`}
      rename={{ send: (label) => rename.mutateAsync({ catalog, id: row.id, label }), pending: rename.isPending }}
      remove={removal}
      onBack={onBack}
      onClose={onClose}
    />
  );
}

/** A service line's name and its split defaults in whole percents, saved in one request (TD-078). */
function ServiceLineForm({ line, rows, remove, onBack, onClose }: { line: ServiceLine; rows: CatalogRow[]; remove: Removal; onBack: () => void; onClose: () => void }) {
  const workTypes = useWorkTypes();
  const createWorkType = useWorkTypeCreator();
  const update = useUpdateServiceLine();
  // Read once: a refetch must not reset what is being typed.
  const [form, setForm] = useState<DefaultsForm>(() => defaultsForm(line));
  // Defaults left alone are not sent, so a rename keeps the stored weights exact.
  const [touched, setTouched] = useState(false);
  const check = defaultsCheck(form);
  const edit = (patch: Partial<DefaultsForm>) => {
    setForm((current) => ({ ...current, ...patch }));
    setTouched(true);
  };
  const words = CATALOG_WORDS["service-lines"];
  return (
    <CatalogRowForm
      row={line}
      rows={rows}
      noun={words.label}
      inUseNote={words.inUse}
      deleteQuestion={`Delete ${line.label}? Nothing uses it.`}
      rename={{ send: (label) => update.mutateAsync({ id: line.id, patch: touched ? { label, ...defaultsPatch(form) } : { label } }), pending: update.isPending }}
      remove={remove}
      blocked={check.blocked}
      dirty={touched}
      size="lg"
      onBack={onBack}
      onClose={onClose}
    >
      {workTypes.data ? (
        <div className="space-y-3">
          <PriceSplitEditor
            percentOnly
            priceMinor={100}
            values={form.pcts}
            onChange={(pcts) => edit({ pcts })}
            workTypes={workTypes.data}
            onCreateWorkType={createWorkType}
            hint={SPLIT_HINT}
          />
          {check.splitError && <p className="text-xs text-red-600">{check.splitError}</p>}
          {hasSplit(form.pcts) && (
            <FormField label="Catch-up rate, %" hint={CATCH_UP_HINT} error={check.catchUpError} className="w-64">
              <input type="number" min={0} max={100} step={1} aria-label="Catch-up rate" className={inputClassName} value={form.catchUp} onChange={(e) => edit({ catchUp: e.target.value })} />
            </FormField>
          )}
        </div>
      ) : (
        <PendingBody failure={workTypes.isError ? "Could not load the work types." : null} retrying={workTypes.isFetching} retry={() => void workTypes.refetch()} />
      )}
    </CatalogRowForm>
  );
}
